import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { authService } from '../../services/auth/authService';
import { DashboardHeader } from '../../components/layout/DashboardHeader/DashboardHeader';
import { DashboardSidebar } from '../../components/layout/DashboardSidebar/DashboardSidebar';
import { createSidebarItems } from '../../components/layout/DashboardSidebar/sidebarItems';
import {
  dashboardService,
  AppointmentWithDetails,
  AvailableSlotsResponse,
} from '../../services/dashboard/dashboardService';
import { formatDateTime } from '../../utils/dateUtils';
import { getCurrentDoctor } from '../../services/doctor-onboarding/doctorOnboardingService';
import { profileService, DoctorPractice } from '../../services/profile/profileService';
import { leaveSlotService, LeaveSlot } from '../../services/availability/leaveSlotService';
import AvailabilityCalendar from '../../components/availability/AvailabilityCalendar/AvailabilityCalendar';
import './EditAppointmentPage.css';

const EditAppointmentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const returnToPatientId = (location.state as { returnToPatientId?: string } | null)?.returnToPatientId;
  const returnToPatientAppointments = () => {
    if (returnToPatientId) {
      navigate(`/patients/${returnToPatientId}?tab=appointments`);
    } else {
      navigate('/appointments');
    }
  };
  const [appointment, setAppointment] = useState<AppointmentWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Two options: Cancel appointment OR Reschedule (then show calendar + clinic + slots)
  const [showRescheduleSection, setShowRescheduleSection] = useState(false);
  const [rescheduleDoctorId, setRescheduleDoctorId] = useState<number | null>(null);
  const [, setRescheduleTimezone] = useState<string>('');
  const [reschedulePractices, setReschedulePractices] = useState<DoctorPractice[]>([]);
  const [rescheduleSelectedPracticeId, setRescheduleSelectedPracticeId] = useState<number | null>(null);
  const [rescheduleSelectedDate, setRescheduleSelectedDate] = useState<string>('');
  const [rescheduleDisplayedMonth, setRescheduleDisplayedMonth] = useState<Date>(() => new Date());
  const [rescheduleLeaveSlots, setRescheduleLeaveSlots] = useState<LeaveSlot[]>([]);
  const [rescheduleCounts, setRescheduleCounts] = useState<Record<string, number>>({});
  const [rescheduleCountsLoading, setRescheduleCountsLoading] = useState(false);
  const [rescheduleSlots, setRescheduleSlots] = useState<AvailableSlotsResponse | null>(null);
  const [rescheduleSlotsLoading, setRescheduleSlotsLoading] = useState(false);
  const [rescheduleSelectedSlot, setRescheduleSelectedSlot] = useState<{ time: string; time_24h: string } | null>(null);
  const [rescheduleType, setRescheduleType] = useState<string>('consultation');
  const [rescheduleError, setRescheduleError] = useState<string | null>(null);
  const [rescheduleDataLoading, setRescheduleDataLoading] = useState(false);

  const loadAppointment = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const apptData = await dashboardService.getAppointmentById(parseInt(id!));
      setAppointment(apptData);
    } catch (err: any) {
      console.error('Failed to load appointment:', err);
      setError(err?.response?.data?.detail || err.message || 'Failed to load appointment');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadAppointment();
  }, [loadAppointment]);

  const handleLogout = () => {
    authService.clearTokens();
    navigate('/login');
  };

  const handleGoToProfile = () => {
    navigate('/profile');
  };

  /** Start reschedule: load doctor, practices, then show calendar/slots section */
  const handleStartReschedule = useCallback(async () => {
    setRescheduleError(null);
    setRescheduleSlots(null);
    setRescheduleSelectedSlot(null);
    setRescheduleDataLoading(true);
    try {
      const [doc, profile, practicesData] = await Promise.all([
        getCurrentDoctor(),
        profileService.getProfile(),
        profileService.getPractices(),
      ]);
      setRescheduleDoctorId(doc.id);
      setRescheduleTimezone(profile.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone);
      setReschedulePractices(practicesData);
      const primary = practicesData.find((p) => p.is_primary) || practicesData[0];
      setRescheduleSelectedPracticeId(primary?.id ?? null);
      const today = new Date();
      const dateStr = today.toISOString().slice(0, 10);
      setRescheduleSelectedDate(dateStr);
      setRescheduleDisplayedMonth(new Date(today.getFullYear(), today.getMonth(), 1));
      setRescheduleLeaveSlots([]);
      setRescheduleCounts({});
      setRescheduleType(appointment?.appointment_type || 'consultation');
      setShowRescheduleSection(true);
    } catch (e: any) {
      setRescheduleError(e?.response?.data?.detail || e?.message || 'Failed to load');
    } finally {
      setRescheduleDataLoading(false);
    }
  }, [appointment?.appointment_type]);

  const rescheduleFormatDateString = useCallback((date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Load slots when reschedule section is open and date/practice selected
  useEffect(() => {
    if (!showRescheduleSection || !rescheduleDoctorId || !rescheduleSelectedDate) return;
    let cancelled = false;
    setRescheduleSlotsLoading(true);
    setRescheduleSlots(null);
    setRescheduleSelectedSlot(null);
    dashboardService
      .getAvailableSlots(
        rescheduleDoctorId,
        rescheduleSelectedDate,
        rescheduleSelectedPracticeId ?? undefined
      )
      .then((data) => {
        if (!cancelled) setRescheduleSlots(data);
      })
      .catch((e: any) => {
        if (!cancelled) setRescheduleError(e?.response?.data?.detail || e?.message || 'Failed to load slots');
      })
      .finally(() => {
        if (!cancelled) setRescheduleSlotsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showRescheduleSection, rescheduleDoctorId, rescheduleSelectedDate, rescheduleSelectedPracticeId]);

  // Load leave slots and appointment counts for reschedule calendar
  useEffect(() => {
    if (!showRescheduleSection) return;
    const month = rescheduleDisplayedMonth;
    const calendar = new Date(month.getFullYear(), month.getMonth(), 1);
    const monthStart = new Date(calendar);
    const monthEnd = new Date(calendar.getFullYear(), calendar.getMonth() + 1, 0);
    const startDateStr = rescheduleFormatDateString(monthStart);
    const endDateStr = rescheduleFormatDateString(monthEnd);
    let cancelled = false;
    setRescheduleCountsLoading(true);
    Promise.all([
      leaveSlotService.getLeaveSlots({ start_date: startDateStr, end_date: endDateStr }),
      leaveSlotService.getAppointmentCounts(startDateStr, endDateStr),
    ])
      .then(([slots, counts]) => {
        if (!cancelled) {
          setRescheduleLeaveSlots(slots);
          setRescheduleCounts((prev) => ({ ...prev, ...counts }));
        }
      })
      .catch((err) => {
        if (!cancelled) console.error('Failed to load calendar data:', err);
      })
      .finally(() => {
        if (!cancelled) setRescheduleCountsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showRescheduleSection, rescheduleDisplayedMonth, rescheduleFormatDateString]);

  const reschedulePracticeDayNames = useMemo(() => {
    const set = new Set<string>();
    const practicesToUse =
      rescheduleSelectedPracticeId != null
        ? reschedulePractices.filter((p) => p.id === rescheduleSelectedPracticeId)
        : reschedulePractices;
    practicesToUse.forEach((practice) => {
      practice.timings?.forEach((t) => {
        if (t.is_available && t.day_of_week) {
          set.add(t.day_of_week.toLowerCase());
        }
      });
    });
    return set;
  }, [reschedulePractices, rescheduleSelectedPracticeId]);

  const rescheduleLeaveSlotsForDate = useCallback(
    (date: Date): LeaveSlot[] => {
      const dateStr = rescheduleFormatDateString(date);
      return rescheduleLeaveSlots.filter((slot) => slot.leave_date === dateStr);
    },
    [rescheduleLeaveSlots, rescheduleFormatDateString]
  );

  const rescheduleDurationInHours = useCallback((start: string, end: string): number => {
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    return (endHour * 60 + endMin - (startHour * 60 + startMin)) / 60;
  }, []);

  const rescheduleHasFullDayLeave = useCallback(
    (date: Date): boolean => {
      const slots = rescheduleLeaveSlotsForDate(date).filter(
        (slot) => slot.status.toLowerCase() !== 'cancelled'
      );
      return (
        slots.some((slot) => slot.start_time === '00:00' && slot.end_time === '23:59') ||
        slots.reduce((total, slot) => {
          return total + rescheduleDurationInHours(slot.start_time, slot.end_time);
        }, 0) >= 8.0
      );
    },
    [rescheduleLeaveSlotsForDate, rescheduleDurationInHours]
  );

  const rescheduleHasPartialLeave = useCallback(
    (date: Date): boolean => {
      const slots = rescheduleLeaveSlotsForDate(date).filter(
        (slot) => slot.status.toLowerCase() !== 'cancelled'
      );
      return slots.length > 0 && !rescheduleHasFullDayLeave(date);
    },
    [rescheduleLeaveSlotsForDate, rescheduleHasFullDayLeave]
  );

  const rescheduleIsDateEnabled = useCallback(
    (date: Date): boolean => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const normalizedDate = new Date(date);
      normalizedDate.setHours(0, 0, 0, 0);
      if (normalizedDate < today) return false;
      if (reschedulePractices.length === 0) return false;
      if (reschedulePracticeDayNames.size === 0) return true;
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayName = dayNames[normalizedDate.getDay()];
      return reschedulePracticeDayNames.has(dayName);
    },
    [reschedulePractices, reschedulePracticeDayNames]
  );

  const handleRescheduleSubmit = async () => {
    if (!id || !rescheduleSelectedDate || !rescheduleSelectedSlot) return;
    try {
      setSubmitting(true);
      setError(null);
      setRescheduleError(null);
      const appointmentDate = `${rescheduleSelectedDate}T${rescheduleSelectedSlot.time_24h}:00`;
      await dashboardService.rescheduleAppointment(
        parseInt(id),
        appointmentDate,
        'Doctor requested reschedule'
      );
      const slotDuration = rescheduleSlots?.available_dates?.find((d) => d.date === rescheduleSelectedDate)?.slot_duration ?? 30;
      const updates: { duration?: number; appointment_type?: string } = {};
      updates.duration = slotDuration;
      if (appointment && rescheduleType !== (appointment.appointment_type || 'consultation')) {
        updates.appointment_type = rescheduleType;
      }
      await dashboardService.updateAppointment(parseInt(id), updates);
      alert('Appointment rescheduled successfully! The patient will be notified.');
      returnToPatientAppointments();
    } catch (err: any) {
      console.error('Failed to reschedule appointment:', err);
      const msg = err?.response?.data?.detail || err?.message || 'Failed to reschedule appointment';
      setError(msg);
      setRescheduleError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelAppointment = async () => {
    if (!id || !appointment) return;
    if (!window.confirm('Are you sure you want to cancel this appointment? The patient will be notified.')) {
      return;
    }
    try {
      setCancelling(true);
      setError(null);
      await dashboardService.updateAppointment(parseInt(id), { status: 'cancelled' });
      alert('Appointment cancelled.');
      returnToPatientAppointments();
    } catch (err: any) {
      console.error('Failed to cancel appointment:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to cancel appointment');
    } finally {
      setCancelling(false);
    }
  };

  const currentPath = location.pathname;
  const sidebarItems = createSidebarItems(navigate, currentPath);

  const canEdit = appointment && (
    appointment.status === 'scheduled' ||
    appointment.status === 'confirmed' ||
    appointment.status === 'reschedule_pending'
  );

  if (loading) {
    return (
      <div className="dashboard">
        <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />
        <div className="dashboard-layout">
          <DashboardSidebar items={sidebarItems} />
          <div className="dashboard-container">
            <main className="dashboard-main-content edit-appointment-content">
              <div className="loading-message">Loading appointment details...</div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  if (error && !appointment) {
    return (
      <div className="dashboard">
        <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />
        <div className="dashboard-layout">
          <DashboardSidebar items={sidebarItems} />
          <div className="dashboard-container">
            <main className="dashboard-main-content edit-appointment-content">
              <div className="error-message">Error: {error}</div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />

      <div className="dashboard-layout">
        <DashboardSidebar items={sidebarItems} />

        <div className="dashboard-container">
          <main className="dashboard-main-content edit-appointment-content">
            {error && (
              <div className="error-alert">
                Error: {error}
              </div>
            )}

            <div className="edit-appointment-card">
              <nav className="detail-breadcrumb" aria-label="Breadcrumb">
                <Link to="/appointments" className="breadcrumb-link">Appointments</Link>
                <span className="breadcrumb-sep" aria-hidden>›</span>
                <span className="breadcrumb-current">Edit</span>
              </nav>
              <div className="edit-appointment-card-header">
                <h1 className="edit-appointment-title">Edit Appointment</h1>
                <button className="cancel-button" onClick={returnToPatientAppointments}>
                  Cancel
                </button>
              </div>

              {appointment && (
                <div className="dashboard-section edit-appointment-card-section">
                  <h2 className="section-title">Original Appointment</h2>
                  <div className="original-appointment-info">
                    <div className="info-row">
                      <span className="info-label">Patient:</span>
                      <span className="info-value">{appointment.patient_name || 'Unknown'}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Date & Time:</span>
                      <span className="info-value">{formatDateTime(appointment.appointment_date, true, 'short')}</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Duration:</span>
                      <span className="info-value">{appointment.duration_minutes ?? appointment.duration ?? 30} minutes</span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Status:</span>
                      <span className="info-value status-badge-original">{appointment.status}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Two options: Cancel appointment OR Reschedule (then show calendar + clinic + slots) */}
              {appointment && canEdit && !showRescheduleSection && (
                <div className="edit-appointment-options">
                  <p className="edit-appointment-options-label">What would you like to do?</p>
                  <div className="edit-appointment-options-buttons">
                    <button
                      type="button"
                      className="cancel-appointment-button"
                      onClick={handleCancelAppointment}
                      disabled={cancelling}
                    >
                      {cancelling ? 'Cancelling...' : 'Cancel Appointment'}
                    </button>
                    <button
                      type="button"
                      className="reschedule-option-button"
                      onClick={handleStartReschedule}
                      disabled={rescheduleDataLoading}
                    >
                      {rescheduleDataLoading ? 'Loading...' : 'Reschedule Appointment'}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {showRescheduleSection && (
              <div className="dashboard-section edit-appointment-reschedule-section">
                <h2 className="section-title">Select New Time</h2>
                {rescheduleError && (
                  <div className="edit-appointment-reschedule-error">{rescheduleError}</div>
                )}
                <div className="edit-appointment-reschedule-form">
                  <div className="edit-appointment-practice-type-row">
                    {reschedulePractices.length > 1 && (
                      <div className="form-group">
                        <label className="edit-appointment-form-label">Clinic / Practice</label>
                        <select
                          value={rescheduleSelectedPracticeId ?? ''}
                          onChange={(e) => {
                            setRescheduleSelectedPracticeId(
                              e.target.value ? Number(e.target.value) : null
                            );
                            setRescheduleSelectedSlot(null);
                          }}
                          className="form-select edit-appointment-practice-select"
                        >
                          {reschedulePractices.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.practice_name ?? `Practice ${p.id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="form-group">
                      <label className="edit-appointment-form-label">Type</label>
                      <select
                        value={rescheduleType}
                        onChange={(e) => setRescheduleType(e.target.value)}
                        className="form-select edit-appointment-type-select"
                      >
                        <option value="consultation">Consultation</option>
                        <option value="online_consultation">Online Consultation</option>
                        <option value="follow_up">Follow-up</option>
                        <option value="hospital_visit">Hospital Visit</option>
                      </select>
                    </div>
                  </div>
                  <div className="edit-appointment-reschedule-two-col">
                    <div className="edit-appointment-reschedule-left">
                      <label className="edit-appointment-form-label">Date</label>
                      <div className="edit-appointment-calendar-wrapper">
                        <AvailabilityCalendar
                          selectedDate={
                            rescheduleSelectedDate
                              ? new Date(rescheduleSelectedDate + 'T12:00:00')
                              : new Date()
                          }
                          displayedMonth={rescheduleDisplayedMonth}
                          leaveSlots={rescheduleLeaveSlots}
                          appointmentCounts={rescheduleCounts}
                          onDateSelect={(date) => {
                            setRescheduleSelectedDate(rescheduleFormatDateString(date));
                            setRescheduleSelectedSlot(null);
                          }}
                          onMonthChange={setRescheduleDisplayedMonth}
                          hasFullDayLeave={rescheduleHasFullDayLeave}
                          hasPartialLeave={rescheduleHasPartialLeave}
                          isDateEnabled={rescheduleIsDateEnabled}
                          isLoadingAppointmentCounts={rescheduleCountsLoading}
                        />
                      </div>
                      {reschedulePracticeDayNames.size > 0 && (
                        <p className="edit-appointment-calendar-hint">
                          Only days when you have practice timings are selectable.
                        </p>
                      )}
                    </div>
                    <div className="edit-appointment-reschedule-right">
                      <label className="edit-appointment-form-label">Available Slots</label>
                      <div className="edit-appointment-slots-legend">
                        <span className="edit-appointment-slots-legend-item">
                          <span className="edit-appointment-slot-legend-dot booked" />
                          Booked
                        </span>
                        <span className="edit-appointment-slots-legend-item">
                          <span className="edit-appointment-slot-legend-dot selected" />
                          Selected
                        </span>
                      </div>
                      <div className="edit-appointment-slots-scroll">
                        {rescheduleSlotsLoading ? (
                          <div className="edit-appointment-slots-loading">Loading slots...</div>
                        ) : rescheduleSlots?.available_slots?.length ? (
                          <div className="edit-appointment-slots-grid">
                            {rescheduleSlots.available_slots.map((slot) => (
                              <button
                                key={slot.time_24h}
                                type="button"
                                className={`edit-appointment-slot-btn ${slot.is_booked ? 'booked' : ''} ${rescheduleSelectedSlot?.time_24h === slot.time_24h ? 'selected' : ''}`}
                                onClick={() =>
                                  !slot.is_booked && setRescheduleSelectedSlot({ time: slot.time, time_24h: slot.time_24h })
                                }
                                disabled={!!slot.is_booked}
                              >
                                {slot.time}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <div className="edit-appointment-no-slots">
                            {rescheduleSlots?.message || 'No slots available for this date. Try another date.'}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="edit-appointment-reschedule-actions">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={() => {
                      setShowRescheduleSection(false);
                      setRescheduleError(null);
                    }}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="submit-button"
                    onClick={handleRescheduleSubmit}
                    disabled={
                      !rescheduleSelectedSlot ||
                      submitting ||
                      rescheduleSlotsLoading
                    }
                  >
                    {submitting ? 'Rescheduling...' : 'Reschedule Appointment'}
                  </button>
                </div>
              </div>
            )}

            {appointment && canEdit && showRescheduleSection && (
              <div className="edit-appointment-actions edit-appointment-actions-inline">
                <button
                  type="button"
                  className="cancel-appointment-button"
                  onClick={handleCancelAppointment}
                  disabled={cancelling || submitting}
                >
                  {cancelling ? 'Cancelling...' : 'Cancel Appointment'}
                </button>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default EditAppointmentPage;
